import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Outlet } from "react-router-dom";

import { Box, CssBaseline, Paper as MuiPaper } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { spacing } from "@mui/system";

import GlobalStyle from "../components/GlobalStyle";
import Navbar from "../components/navbar/Navbar";
import dashboardItems from "../components/sidebar/dashboardItems";
import Sidebar, { RAIL_WIDTH } from "../components/sidebar/Sidebar";
import Footer from "../components/Footer";
import Settings from "../components/Settings";

const drawerWidth = 258;

// Whether the sidebar is kept open, remembered on this device. Otherwise
// it autohides to an icon bar that opens on hover.
const PINNED_KEY = "sidebar.pinned";

const loadPinned = () => {
  try {
    return window.localStorage.getItem(PINNED_KEY) === "true";
  } catch (e) {
    return false;
  }
};

const Root = styled.div`
  display: flex;
  min-height: 100vh;
`;

// The room the sidebar takes beside the page: its full width when kept
// open, the icon bar's when it autohides (it opens over the page).
const Drawer = styled.div`
  ${(props) => props.theme.breakpoints.up("md")} {
    width: ${(props) => props.reserved}px;
    flex-shrink: 0;
    transition: width 200ms ease;
  }
`;

const AppContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  /* A flex item's floor is its content's min-content width, so a grid
     wider than the viewport (the COC worksheet's ~40 columns) would push
     this to 100vw and scroll the whole page instead of the grid. */
  min-width: 0;
`;

const Paper = styled(MuiPaper)(spacing);

const MainContent = styled(Paper)`
  flex: 1;
  background: ${(props) => props.theme.palette.background.default};

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }

  .MuiPaper-root .MuiPaper-root {
    box-shadow: none;
    width: 100%;
  }
`;

const Dashboard = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pinned, setPinned] = useState(loadPinned);

  useEffect(() => {
    try {
      window.localStorage.setItem(PINNED_KEY, String(pinned));
    } catch (e) {
      // Storage may be unavailable (private window); the choice is a nicety.
    }
  }, [pinned]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));

  return (
    <Root>
      <CssBaseline />
      <GlobalStyle />
      <Drawer reserved={pinned ? drawerWidth : RAIL_WIDTH}>
        <Box sx={{ display: { xs: "block", lg: "none" } }}>
          <Sidebar
            PaperProps={{ style: { width: drawerWidth } }}
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            items={dashboardItems}
          />
        </Box>
        <Box sx={{ display: { xs: "none", md: "block" } }}>
          <Sidebar
            PaperProps={{ style: { width: drawerWidth } }}
            items={dashboardItems}
            rail={!pinned}
            onToggleRail={() => setPinned((open) => !open)}
          />
        </Box>
      </Drawer>
      <AppContent>
        <Navbar onDrawerToggle={handleDrawerToggle} />
        <MainContent p={isLgUp ? 12 : 5}>
          {children}
          <Outlet />
        </MainContent>
        <Footer />
      </AppContent>
      <Settings />
    </Root>
  );
};

export default Dashboard;
